// Strafe helper in the style of CGaz, drawn on a thin canvas strip under the
// crosshair. Colours come from the theme tokens.

import { el } from './dom';

export interface CgazState {
  /** View yaw in degrees. */
  yaw: number;
  velocity: [number, number];
  move: { forwardmove: number; rightmove: number; upmove: number };
  sprint: boolean;
  onGround: boolean;
  frametime: number;
  /** Horizontal fov (4:3) used to map angles to the screen. */
  hfov: number;
}

/** "#rrggbb" / "rgb(r, g, b)" -> [r, g, b]. */
function parseColor(css: string, fallback: [number, number, number]): [number, number, number] {
  const s = css.trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(s);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [n >> 16, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\((\d+)[ ,]+(\d+)[ ,]+(\d+)/i.exec(s);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : fallback;
}

export class Cgaz {
  readonly canvas = el('canvas', 'hud-cgaz');
  private readonly ctx = this.canvas.getContext('2d')!;
  private gain: [number, number, number] = [78, 212, 154];
  private tick = '#e6eaf1';
  private center = '#f0a030';

  /** Re-reads the colours from the CSS tokens (after a theme change). */
  readColors(): void {
    const style = getComputedStyle(document.documentElement);
    this.gain = parseColor(style.getPropertyValue('--success'), this.gain);
    this.tick = style.getPropertyValue('--text').trim() || this.tick;
    this.center = style.getPropertyValue('--accent').trim() || this.center;
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = 24;
    this.canvas.style.top = `${Math.round(height * 0.53)}px`;
  }

  /**
   * Shows, around the crosshair, which view yaws make the current wish
   * direction gain speed (PM_Accelerate maths): dim from the first gaining
   * angle to the optimum, bright from the optimum to where gaining stops; the
   * light tick is the optimal angle.
   */
  draw(state: CgazState | null): void {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (!state) return;
    const { move } = state;
    const speed = Math.hypot(state.velocity[0], state.velocity[1]);
    if (speed < 1 || (!move.forwardmove && !move.rightmove)) return;

    // PM_CmdScale
    const max = Math.max(Math.abs(move.forwardmove), Math.abs(move.rightmove), Math.abs(move.upmove));
    const total = Math.hypot(move.forwardmove, move.rightmove, move.upmove);
    // |forward * fmove + right * smove| * scale
    const wishspeed = ((320 * max) / (127 * total)) * (state.sprint ? 1.1 : 0.8) * Math.hypot(move.forwardmove, move.rightmove);
    const accel = (state.onGround ? 10 : 1) * state.frametime * wishspeed;

    const clamp = (x: number) => Math.max(-1, Math.min(1, x));
    const thMin = Math.acos(clamp(wishspeed / speed));
    const thOpt = Math.acos(clamp((wishspeed - accel) / speed));
    const thMax = Math.acos(clamp(-accel / (2 * speed)));
    const deg = 180 / Math.PI;
    const velYaw = Math.atan2(state.velocity[1], state.velocity[0]) * deg;
    // wish direction relative to the view: forward = 0, right strafe = -90
    const keyOffset = Math.atan2(-move.rightmove, move.forwardmove) * deg;
    const half = state.hfov / 2;
    const toX = (wishYaw: number) => {
      const viewYaw = wishYaw - keyOffset;
      const d = ((((viewYaw - state.yaw) % 360) + 540) % 360) - 180;
      return w / 2 - (d / half) * (w / 2);
    };
    // speed gained per frame with the wish direction at angle th from the velocity
    const gain = (th: number) => {
      const add = wishspeed - speed * Math.cos(th);
      const a = add <= 0 ? 0 : Math.min(accel, add);
      return Math.sqrt(speed * speed + 2 * speed * a * Math.cos(th) + a * a) - speed;
    };
    const best = gain(thOpt);
    if (best <= 0) return;
    const STEPS = 48;
    const [r, g, b] = this.gain;
    for (const side of [1, -1]) {
      // shade the gaining range by how much speed it gives
      for (let i = 0; i < STEPS; i++) {
        const a0 = thMin + ((thMax - thMin) * i) / STEPS;
        const a1 = thMin + ((thMax - thMin) * (i + 1)) / STEPS;
        const x0 = toX(velYaw + side * a0 * deg);
        const x1 = toX(velYaw + side * a1 * deg);
        if (Math.abs(x1 - x0) > w / 2) continue; // wrapped around the screen edge
        const k = Math.max(0, gain((a0 + a1) / 2) / best);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${(0.1 + 0.75 * k * k).toFixed(3)})`;
        ctx.fillRect(Math.min(x0, x1), 4, Math.abs(x1 - x0) + 0.5, h - 8);
      }
      const x = toX(velYaw + side * thOpt * deg);
      ctx.fillStyle = this.tick;
      ctx.fillRect(x - 1, 0, 2, h);
    }
    ctx.fillStyle = this.center;
    ctx.fillRect(w / 2 - 1, 2, 2, h - 4);
  }
}
